import { useCallback, useRef } from 'react';
import { CONTACT_PROFILE_CONFIG, PROFILE_REQUESTS } from 'features/inventory-profiles/constants';
import { buildProfileEndpoint } from 'features/inventory-profiles/utils';
import { useCreateProfileView } from 'hooks/createProfileView';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import API from 'services/API';
import { UIStatus } from 'UI/constants/status';

const INITIAL_CONTACT = {};

/**
 * Contact profile data. Tracks the view once the contact has loaded.
 * @param {number|string} id Contact (name) id
 * @returns {{
 *   contact: Object,
 *   error: { request: string, statusCode?: number } | null,
 *   isLoading: boolean,
 *   refresh: () => void,
 *   reload: () => void
 * }}
 */
export const useContactProfile = id => {
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const latestRequestRef = useRef(null);

  const contactEndpoint = buildProfileEndpoint({
    entity: CONTACT_PROFILE_CONFIG,
    id,
    isEnabled: checkIfFeatureFlagEnabled
  });

  const loadProfile = useCallback(() => {
    const profileRequest = API.get(contactEndpoint, { apiVersion: 2 });
    latestRequestRef.current = profileRequest;

    const resolveWithLatestRequest = () => latestRequestRef.current;

    return profileRequest.then(resolveWithLatestRequest, resolveWithLatestRequest);
  }, [contactEndpoint]);

  const { state, refreshData } = useFetchWithStatusV2({
    promise: id ? loadProfile : undefined,
    initialState: INITIAL_CONTACT
  });

  const { status, results, responseStatusCode } = state;
  const isLoading = status === UIStatus.Loading;
  const contact = isLoading ? INITIAL_CONTACT : results ?? INITIAL_CONTACT;

  useCreateProfileView({ id: contact.id, endpoint: CONTACT_PROFILE_CONFIG.trackingUrl });

  return {
    contact,
    error:
      status === UIStatus.Error
        ? { request: PROFILE_REQUESTS.Profile, statusCode: responseStatusCode }
        : null,
    isLoading,
    refresh: () => refreshData(false),
    reload: () => refreshData()
  };
};
